import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { DataGrid } from "@mui/x-data-grid";
import { FC, useEffect, useState } from "react";
import { WorkspacesTableRowDefinition } from "../types";
import { ActionOption } from "./ActionCell/utils";
import { createWorkspacesColumns } from "./utils";

interface WorkspacesTableProps {
  rows: WorkspacesTableRowDefinition[];
  isLoading?: boolean;
  onRowClick?: (row: WorkspacesTableRowDefinition) => void;
  onActionClick: (
    action: ActionOption,
    row: WorkspacesTableRowDefinition
  ) => void;
  showOwner?: boolean;
}

/**
 * Trick to perfect scroll table
 * Adjust this value if headers height change
 */
const HEIGHT_OFFSET = 280;

export const WorkspacesTable: FC<WorkspacesTableProps> = (props) => {
  const [availableHeight, setAvailableHeight] = useState<number>(
    window.innerHeight - HEIGHT_OFFSET
  );

  useEffect(() => {
    const handleResize = () => {
      setAvailableHeight(window.innerHeight - HEIGHT_OFFSET);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const columns = createWorkspacesColumns({
    onActionClick: props.onActionClick,
    rows: props.rows,
    showOwner: props.showOwner,
  });

  const NoRowsOverlay = () => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="100%"
    >
      <Typography align="center" color="text.secondary">
        There are no Projects currently set up in your organization.
        <br />
        Please use the 'Create Project' button to get started!
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ maxHeight: availableHeight, overflowY: "auto" }}>
      <DataGrid
        data-testid="workspace-table"
        rows={props.rows}
        loading={props.isLoading}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: "lastUpdate", sort: "desc" }],
          },
        }}
        hideFooter
        disableColumnMenu
        sortingOrder={["asc", "desc"]}
        onRowClick={({ row }) => {
          if (props.onRowClick) {
            props.onRowClick(row);
          }
        }}
        disableRowSelectionOnClick
        slots={{
          noRowsOverlay: NoRowsOverlay,
        }}
        sx={{
          "&, [class^=MuiDataGrid]": {
            border: "none",
            cursor: "pointer",
          },
          "& .BW-MuiDataGrid-cell": {
            border: "none",
          },
          "& .BW-MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
          "& .BW-MuiDataGrid-row": {
            border: "none",
            cursor: props.onRowClick ? "pointer" : "default",
          },
          "& .BW-MuiDataGrid-columnHeader": {
            border: "none !important",
          },
          "& .BW-MuiDataGrid-columnHeader:focus-within": {
            outline: "none",
          },
          "& .BW-MuiDataGrid-columnSeparator": {
            cursor: "col-resize",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.1)",
            },
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none !important",
          },
        }}
      />
    </Box>
  );
};
